import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  OutlinedInputProps,
  TextFieldProps,
  Tooltip,
  styled
} from "@mui/material";
import React, { ChangeEvent, forwardRef, useContext } from "react";
import { IMaskInput } from "react-imask";

import { SessionContext } from "../../Context/SessionContext";
// import { UserContext } from "../../Context/UserContext";
import { Customer } from "../../Models/Customer";
// import { PhoneNumberType } from "../../Models/PhoneNumber";
import { ValidatedTextFieldProps } from "./ValidatedTextField";

export interface PhoneNumberFieldProps
  extends Omit<OutlinedInputProps, "onChange">,
    Pick<ValidatedTextFieldProps, "onChange" | "onAccepted">,
    Pick<TextFieldProps, "helperText"> {
  /** The key of the customer phone number prop which should be associated with errors occurring with the value. */
  customerKey: keyof Pick<Customer, "phoneNumber" | "workPhoneNumber" | "homePhoneNumber">;
}

const validRegex = /\d{10}/;

/** A text field for handling masked phone number entries. Follows the `ValidatedTextField` behaviors. */
const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  customerKey,
  error,
  helperText,
  label,
  value,
  onChange,
  onAccepted,
  ...rest
}) => {
  const { phoneNumberErrors } = useContext(SessionContext);
  const onFieldChange = (value: string) => {
    onChange && onChange(value);
    if (validRegex.test(value) && onAccepted) onAccepted(value);
  };
  const lengthError = String(value).length > 0 && String(value).length < 10 && "Invalid Format";
  const phoneNumberError = phoneNumberErrors[customerKey];
  const errorConfig = phoneNumberError
    ? {
        helperText: (
          <Box display="flex" alignItems="center" component="span">
            {`${label} is not allowed.`}
            {phoneNumberError.note && <InfoIcon sx={{ fontSize: ".95rem", ml: "0.3rem" }} />}
          </Box>
        ),
        helperTextTitle: phoneNumberError.note
      }
    : undefined;
  const errorHelperText = errorConfig?.helperTextTitle ? errorConfig?.helperTextTitle.split("\r\n") : undefined;
  return (
    <FormControl variant="outlined" margin="dense" size="small" fullWidth sx={{ m: 0 }}>
      {label && <InputLabel htmlFor="formatted-text-mask-input">{label}</InputLabel>}
      <StyledOutlinedInput
        margin="dense"
        size="small"
        {...rest}
        error={error || Boolean(lengthError || phoneNumberError)}
        value={value}
        label={label}
        onPaste={e => onFieldChange(e.clipboardData.getData("").replace(/[^0-9]/g, ""))}
        onChange={e => onFieldChange(e.target.value.replace(/[^0-9]/g, ""))}
        inputComponent={PhoneFieldMasked as never}
        sx={{ display: "flex", alignItems: "center" }}
      />
      <Tooltip title={errorHelperText?.map((e, key) => <div key={key}>{e}</div>) ?? ""} arrow>
        <StyledFormHelperText variant="outlined" error={error || Boolean(lengthError || phoneNumberError)}>
          {helperText || lengthError || errorConfig?.helperText || " "}
        </StyledFormHelperText>
      </Tooltip>
    </FormControl>
  );
};

export const PhoneFieldMasked = forwardRef<HTMLElement, OutlinedInputProps>(function PhoneFieldMasked(
  // eslint-disable-next-line react/prop-types
  { onChange, ...rest },
  ref
) {
  return (
    <IMaskInput
      {...(rest as Record<string, unknown>)}
      mask="(#00) 000-0000"
      definitions={{ "#": /[1-9]/ }}
      inputRef={ref as never}
      onChange={e => onChange && onChange(e as ChangeEvent<HTMLInputElement>)}
      overwrite
    />
  );
});

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  "&": { userSelect: "none" },
  "&.Mui-error": { margin: `${theme.spacing(-0.2)} 0 ${theme.spacing(-0.6)} ${theme.spacing(1.9)}` }
}));
const StyledOutlinedInput = styled(OutlinedInput)(({ error }) => ({
  "& .MuiOutlinedInput-input": { borderWidth: error ? ".15em" : undefined }
}));

export default PhoneNumberField;
